/**
 * Horizontal strip of deterministic insight cards (correlations, records,
 * patterns) surfaced on Today and Trends. Dismiss and thumbs are optimistic:
 * the UI updates immediately and rolls back if the write fails. When there are
 * no cards the component renders nothing at all — never an empty shell.
 */
import { useEffect, useState } from 'react';
import { getInsights, dismissInsight, rateInsight, type InsightCard } from '../api.js';
import { slotColor } from '../metrics.js';
import { useThemeMode } from '../lib/useThemeMode.js';
import { Icon } from '../ui/index.js';

/** One hue per kind, drawn from the categorical palette (no new colours). */
const KIND_SLOT: Record<InsightCard['kind'], number> = {
  correlation: 1, // blue
  record: 4, // green — an achievement
  pattern: 5, // indigo
  weekly: 3, // amber — the assistant's week narrative, a warm standout
};
const KIND_LABEL: Record<InsightCard['kind'], string> = {
  correlation: 'Correlation',
  record: 'Record',
  pattern: 'Pattern',
  weekly: 'This week',
};

/** Weekly cards lead the strip; everything else keeps its server order. */
function sortWeeklyFirst(cards: readonly InsightCard[]): InsightCard[] {
  return [...cards].sort(
    (a, b) => Number(b.kind === 'weekly') - Number(a.kind === 'weekly'),
  );
}

/** Bullet lines render as a list; a long paragraph is cut to 3 lines behind "More". */
function InsightBody({ body }: { body: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const lines = body.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length > 1 && lines.every((l) => /^[•\-*]\s/.test(l))) {
    return (
      <ul className="hb-insight__bullets">
        {lines.map((l, i) => (
          <li key={i}>{l.replace(/^[•\-*]\s+/, '')}</li>
        ))}
      </ul>
    );
  }
  const long = body.length > 180;
  return (
    <>
      <p className={`hb-insight__body${long && !open ? ' hb-insight__body--clamp' : ''}`}>{body}</p>
      {long && (
        <button className="hb-insight__more" onClick={() => setOpen(!open)}>
          {open ? 'Less' : 'More'}
        </button>
      )}
    </>
  );
}

function Thumb({ down = false }: { down?: boolean }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" style={down ? { transform: 'rotate(180deg)' } : undefined}>
      <path
        d="M7 10v11M15 5.9 14 10h5.8a2 2 0 0 1 1.9 2.6l-2.3 7A2 2 0 0 1 17.5 21H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h2.8a2 2 0 0 0 1.8-1.1L12 2a3.1 3.1 0 0 1 3 3.9Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Highlights: at most `limit` cards (Apple Health "Highlights"). Each card is
 * the app telling him one thing — kind, one line, one short body — with a
 * quiet dismiss and a helpful / not-helpful pair.
 */
export default function InsightCards({ limit, title = 'Highlights' }: { limit?: number; title?: string } = {}): JSX.Element | null {
  const mode = useThemeMode();
  const [cards, setCards] = useState<InsightCard[] | null>(null);

  useEffect(() => {
    let alive = true;
    getInsights()
      .then((c) => {
        if (alive) setCards(c);
      })
      .catch(() => {
        if (alive) setCards([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!cards || cards.length === 0) return null;

  // `cards` is non-null below this guard; each handler snapshots it so a failed
  // write can roll the optimistic update back. Side effects stay OUT of the
  // setState updater (which StrictMode double-invokes in dev).
  const snapshot = cards;

  function dismiss(id: string): void {
    setCards(snapshot.filter((c) => c.id !== id));
    dismissInsight(id).catch(() => setCards(snapshot));
  }

  function rate(id: string, rating: 1 | -1): void {
    setCards(snapshot.map((c) => (c.id === id ? { ...c, rating } : c)));
    rateInsight(id, rating).catch(() => setCards(snapshot));
  }

  const shown = sortWeeklyFirst(cards).slice(0, limit ?? cards.length);

  return (
    <section className="hb-insights" aria-label={title}>
      <div className="ui-group__head">
        <h2 className="ui-group__title">{title}</h2>
      </div>
      <div className="hb-insights__list" role="list" aria-label="Insights">
        {shown.map((card) => (
          <article
            className="hb-insight"
            role="listitem"
            key={card.id}
            style={{ ['--insight-accent' as string]: slotColor(KIND_SLOT[card.kind], mode) }}
          >
            <div className="hb-insight__head">
              <span className="hb-insight__kind">
                <span className="ui-dot" style={{ background: 'var(--insight-accent)' }} aria-hidden="true" />
                {KIND_LABEL[card.kind]}
              </span>
              <button className="hb-insight__dismiss" aria-label="Dismiss insight" onClick={() => dismiss(card.id)}>
                <Icon name="x" size={16} />
              </button>
            </div>
            <h3 className="hb-insight__title">{card.title}</h3>
            <InsightBody body={card.body} />
            <div className="hb-insight__rate">
              <button
                className={`hb-insight__thumb${card.rating === 1 ? ' hb-insight__thumb--on' : ''}`}
                aria-label="Helpful"
                aria-pressed={card.rating === 1}
                onClick={() => rate(card.id, 1)}
              >
                <Thumb />
              </button>
              <button
                className={`hb-insight__thumb${card.rating === -1 ? ' hb-insight__thumb--on' : ''}`}
                aria-label="Not helpful"
                aria-pressed={card.rating === -1}
                onClick={() => rate(card.id, -1)}
              >
                <Thumb down />
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
